import { VEC2, type Vector } from '@/utils/math/vector'

export type AABB = {
  min: Vector
  max: Vector
}

export const pointVSAABB = (point: Vector, bounds: AABB): boolean => {
  if (point.x < bounds.min.x || point.x > bounds.max.x) return false
  if (point.y < bounds.min.y || point.y > bounds.max.y) return false
  return true
}

export const getElementAABB = (el: Element): AABB => {
  const rect = el.getBoundingClientRect()
  const min = VEC2(rect.x, rect.y)
  const max = min.add(VEC2(rect.width, rect.height))
  return { min, max }
}

export const AABBvsAABB = (a: AABB, b: AABB): boolean => {
  if (a.max.x < b.min.x || a.min.x > b.max.x) return false
  if (a.max.y < b.min.y || a.min.y > b.max.y) return false
  if (a.max.z < b.min.z || a.min.z > b.max.z) return false
  return true
}

export const getAABBPoints = (a: AABB): Vector[] => {
  return [
    VEC2(a.min.x, a.min.y),
    VEC2(a.max.x, a.min.y),
    VEC2(a.max.y, a.max.y),
    VEC2(a.min.y, a.max.y)
  ]
}

export const SAT = (a: AABB, b: AABB) => {
  const INF = 60000;
  const LARGE = INF * 0.5;
  const pointsA = getAABBPoints(a)
  const pointsB = getAABBPoints(b)
  const axises: Vector[] = [VEC2(1, 0), VEC2(0, 1)]

  const project = (axis: Vector, points: Vector[]) => {
    let min = INF
    let max = -INF
    for (let j = 0; j < points.length; j++) {
      const v = points[j]
      const dot = axis.dot(v)
      if (dot > max) {
        max = dot
      }
      if (dot < min) {
        min = dot
      }
    }
    return VEC2(min, max)
  }

  const checkAxis = (pointsA: Vector[], pointsB: Vector[], axis: Vector) => {
    const p1 = project(axis, pointsA)
    const p2 = project(axis, pointsB)
    const minA = p1.x
    const maxA = p1.y
    const minB = p2.x
    const maxB = p2.y
    const sign = minA < minB ? 1.0 : -1.0
    const overlap = Math.min(maxA, maxB) - Math.max(minA, minB)
    return {
      intersects: (minB <= maxA && minA <= maxB) && (p1.x < LARGE && p1.y < LARGE && p2.x < LARGE && p2.y < LARGE),
      sign,
      overlap
    }
  }

  let minOverlap = INF
  let normal = VEC2(0, 0)
  let intersects: boolean = false

  for (let i = 0; i < axises.length; i++) {
    const axis = axises[i]
    const check = checkAxis(pointsA, pointsB, axis)
    if (check.intersects) {
      if (check.overlap < minOverlap) {
        normal = axis.scale(check.sign);
        minOverlap = check.overlap
        intersects = true
      }
    }
  }

  return {
    intersects,
    minOverlap,
    normal
  }
}
